import { inject, provideAppInitializer } from '@angular/core'

import { RuntimeConfigService } from '@yoshu-lib/core/config/runtime-config.service'
import { DEFAULT_FEATURES, FeatureMap } from '@yoshu-lib/core/feature-flags/feature-flags.model'
import { FeatureFlagsService } from '@yoshu-lib/core/feature-flags/feature-flags.service'
import { Environment, ENVIRONMENT } from '@yoshu-lib/core/tokens/environment.token'

import { environment } from '@coty/env/environment'

/**
 * Holds the merged environment configuration.
 * Build-time values are used as defaults, but apiUrl and socketUrl
 * can be overridden at runtime via /config.json.
 */
const mergedEnvironment: Environment = {
  ...environment,
  features: { ...DEFAULT_FEATURES, ...environment.features } as FeatureMap
}

/**
 * Factory function that loads runtime config and merges it with build-time environment.
 */
async function initializeEnvironment(
  runtimeConfigService: RuntimeConfigService,
  featureFlagsService: FeatureFlagsService
) {
  const runtimeConfig = await runtimeConfigService.loadConfig()

  if (runtimeConfig.apiUrl) {
    mergedEnvironment.apiUrl = runtimeConfig.apiUrl
  }
  if (runtimeConfig.socketUrl) {
    mergedEnvironment.socketUrl = runtimeConfig.socketUrl
  }
  if (runtimeConfig.tenantId) {
    mergedEnvironment.tenantId = runtimeConfig.tenantId
  }
  if (runtimeConfig.features) {
    ;(mergedEnvironment as { features: FeatureMap }).features = {
      ...mergedEnvironment.features,
      ...runtimeConfig.features
    } as FeatureMap
    featureFlagsService.update(runtimeConfig.features as Partial<FeatureMap>)
  }
  runtimeConfigService.markConfigApplied()
}

/**
 * Provides the environment configuration for the application.
 *
 * This function returns providers that:
 * 1. Load runtime config from /config.json at app initialization
 * 2. Merge runtime config with build-time environment
 * 3. Provide the merged environment via the ENVIRONMENT token
 *
 */
export const provideEnvironment = () => {
  return [
    provideAppInitializer(() =>
      initializeEnvironment(inject(RuntimeConfigService), inject(FeatureFlagsService))
    ),
    {
      provide: ENVIRONMENT,
      useFactory: () => mergedEnvironment
    }
  ]
}
